/* ============================================================================
   LeaveReasonPicker: the optional "Why are you leaving?" on the delete screen
   (owner ask, 2026-10-01).

   One tap, picture-first: each reason is a tile with its icon, the way the
   type and region filters are drawn. Tapping the chosen tile again clears it.
   Nothing here is required; the delete button works with no answer at all
   (Apple 5.1.1(v): deletion must stay easy).

   "I want to start over" is the one answer with a better door than deletion:
   Reset Nexus Dex clears the dex and keeps the account, so the tile offers it
   inline.
   ========================================================================== */
import {
  Eye,
  HelpCircle,
  type LucideIcon,
  PuzzleIcon,
  RotateCcw,
  ScanLine,
  Settings2,
  Shield,
} from "lucide-react";
import { LEAVE_DETAIL_MAX, LEAVE_REASONS, LEAVE_REASON_LABELS, type LeaveReason } from "@/lib/leaveReasons";

const ICONS: Record<LeaveReason, LucideIcon> = {
  scanner: ScanLine,
  trying: Eye,
  setup: Settings2,
  missing: PuzzleIcon,
  fresh_start: RotateCcw,
  privacy: Shield,
  other: HelpCircle,
};

const DETAIL_PROMPT: Record<LeaveReason, string> = {
  scanner: "Which screen did it fail on, and what happened? (optional)",
  trying: "Anything that would have made you stay? (optional)",
  setup: "Which step felt like too much? (optional)",
  missing: "What were you hoping it would do? (optional)",
  fresh_start: "Anything else? (optional)",
  privacy: "What worried you? (optional)",
  other: "Tell us in a line (optional)",
};

export function LeaveReasonPicker({
  reason,
  detail,
  onReason,
  onDetail,
  onResetInstead,
  disabled,
}: {
  reason: LeaveReason | null;
  detail: string;
  onReason: (r: LeaveReason | null) => void;
  onDetail: (d: string) => void;
  /** Swap to Reset Nexus Dex (offered for "I want to start over"). */
  onResetInstead?: () => void;
  disabled?: boolean;
}) {
  return (
    <div className="mt-3" data-testid="leave-reason-picker">
      <p className="text-[12px] font-semibold" style={{ color: "var(--text-ink-bright)" }}>
        Why are you leaving? <span className="font-normal text-muted-foreground">(optional)</span>
      </p>
      <div className="mt-2 grid grid-cols-2 gap-1.5" role="group" aria-label="Why are you leaving?">
        {LEAVE_REASONS.map((r) => {
          const Icon = ICONS[r];
          const active = reason === r;
          return (
            <button
              key={r}
              type="button"
              aria-pressed={active}
              disabled={disabled}
              onClick={() => onReason(active ? null : r)}
              data-testid={`leave-reason-${r}`}
              className="rounded-xl px-2.5 py-2 flex items-center gap-2 text-left min-w-0"
              style={{
                background: active ? "var(--accent-fill-glow-subtle)" : "var(--surface-dark-9)",
                border: `${active ? 2 : 1}px solid ${active ? "var(--accent-fill)" : "var(--accent-fill-glow-subtle)"}`,
              }}
            >
              <Icon
                className="w-4 h-4 shrink-0"
                style={{ color: active ? "var(--accent-fill)" : "var(--text-ink-faint)" }}
                aria-hidden
              />
              <span
                className="text-[11px] font-semibold leading-tight"
                style={{ color: active ? "var(--text-ink-bright)" : "var(--text-ink-faint)" }}
              >
                {LEAVE_REASON_LABELS[r]}
              </span>
            </button>
          );
        })}
      </div>

      {reason === "fresh_start" && onResetInstead && (
        <div className="mt-2 rounded-xl p-2.5 surface-elev flex items-center gap-2" data-testid="leave-reason-reset-offer">
          <RotateCcw className="w-4 h-4 shrink-0" style={{ color: "var(--accent-fill)" }} aria-hidden />
          <p className="text-[11px] text-muted-foreground leading-snug flex-1">
            Reset Nexus Dex clears your Pokédex and keeps your account, friends and Pro.
          </p>
          <button
            type="button"
            onClick={onResetInstead}
            disabled={disabled}
            className="text-[11px] font-semibold underline shrink-0"
            style={{ color: "var(--accent-fill)" }}
            data-testid="button-leave-reset-instead"
          >
            Reset instead
          </button>
        </div>
      )}

      {/* Shown once a reason is picked, and kept while it holds text so a
          note is never sent from a box the trainer can no longer see. */}
      {(reason || detail) && (
        <textarea
          value={detail}
          onChange={(e) => onDetail(e.target.value.slice(0, LEAVE_DETAIL_MAX))}
          disabled={disabled}
          rows={2}
          maxLength={LEAVE_DETAIL_MAX}
          placeholder={reason ? DETAIL_PROMPT[reason] : DETAIL_PROMPT.other}
          className="mt-2 w-full rounded-xl px-3 py-2 text-[12px] bg-transparent resize-none"
          style={{ border: "1px solid var(--accent-fill-glow-subtle)", color: "var(--text-ink-bright)" }}
          data-testid="leave-reason-detail"
        />
      )}
    </div>
  );
}
